<template>
  <v-container fluid class="customer-dashboard">
    <v-row v-if="loading" class="mt-8" justify="center">
      <v-skeleton-loader type="image, article, card" width="100%" />
    </v-row>

    <template v-else>
      <v-row dense class="mt-6">
        <v-col cols="12">
          <v-card class="dashboard-hero" elevation="0">
            <v-card-text class="dashboard-hero__content">
              <div>
                <div class="dashboard-hero__eyebrow">Cockpit do aluno</div>
                <div class="text-h5 font-weight-bold mt-1">
                  {{ greetingTitle }}
                </div>
                <div class="dashboard-hero__subtitle mt-2">
                  {{ greetingSubtitle }}
                </div>
              </div>

              <v-avatar size="64" class="dashboard-hero__avatar">
                <v-icon size="34" color="white">mdi-school-outline</v-icon>
              </v-avatar>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row dense class="mt-4">
        <v-col cols="12" lg="7">
          <v-card class="dashboard-surface" elevation="0">
            <v-card-text class="pa-5">
              <div class="dashboard-progress-head">
                <div class="dashboard-section__title">Meu progresso</div>
                <div class="dashboard-section__subtitle">
                  {{
                    dashboard.module?.title
                      ? `Módulo atual: ${dashboard.module.title}`
                      : "Sua trilha aparecerá aqui assim que for liberada."
                  }}
                </div>
              </div>

              <div class="dashboard-donuts">
                <div class="dashboard-donut">
                  <div class="dashboard-donut__ring" :style="overallDonutStyle">
                    <div class="dashboard-donut__inner">
                      <div class="dashboard-donut__value">
                        {{
                          formatPercentage(overallProgress.completedPercentage)
                        }}
                      </div>
                    </div>
                  </div>
                  <div class="dashboard-donut__label">Geral</div>
                  <div class="dashboard-donut__meta">
                    {{ overallProgress.completedCount }}/{{
                      overallProgress.totalLessons
                    }}
                    aulas
                  </div>
                </div>

                <div class="dashboard-donut">
                  <div class="dashboard-donut__ring" :style="donutStyle">
                    <div class="dashboard-donut__inner">
                      <div class="dashboard-donut__value">
                        {{ formatPercentage(progress.completedPercentage) }}
                      </div>
                    </div>
                  </div>
                  <div class="dashboard-donut__label">Nível atual</div>
                  <div class="dashboard-donut__meta">
                    {{ progress.completedCount }}/{{ progress.totalLessons }}
                    aulas
                  </div>
                </div>
              </div>

              <div v-if="hasOverallProgress" class="dashboard-levels">
                <div class="dashboard-levels__title">Progresso por nível</div>
                <div class="dashboard-levels__grid">
                  <div
                    v-for="level in overallProgress.levels"
                    :key="`level-${level.level}`"
                    class="dashboard-levels__item"
                  >
                    <div class="dashboard-levels__head">
                      <span class="dashboard-levels__name">
                        {{ level.title }}
                      </span>
                      <span class="dashboard-levels__meta">
                        {{ formatPercentage(level.completedPercentage) }}
                      </span>
                    </div>
                    <v-progress-linear
                      :value="level.completedPercentage"
                      :color="levelBarColor(level.completedPercentage)"
                      height="8"
                      rounded
                      background-color="rgba(244, 114, 182, 0.14)"
                    ></v-progress-linear>
                  </div>
                </div>
              </div>

              <v-alert v-else type="info" text dense class="mb-0 mt-4">
                Seu progresso aparecerá aqui assim que seu nivelamento e os
                módulos estiverem disponíveis.
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" lg="5">
          <div class="dashboard-side-column">
            <v-card class="dashboard-surface dashboard-billing" elevation="0">
              <v-card-text class="pa-5">
                <div class="dashboard-section__title mb-3">
                  <v-icon color="primary-500" class="mr-2"
                    >mdi-wallet-outline</v-icon
                  >
                  Mensalidades
                </div>

                <div v-if="billingReminder" class="dashboard-billing__item">
                  <div class="dashboard-billing__date-pill">
                    <div class="dashboard-billing__weekday">Venc.</div>
                    <div class="dashboard-billing__day">
                      {{ formatDayMonth(billingReminder.dueDate) }}
                    </div>
                  </div>

                  <div class="dashboard-billing__content">
                    <div class="dashboard-billing__title">
                      {{ formatCurrency(billingReminder.amount) }}
                    </div>
                    <div class="dashboard-billing__meta">
                      {{ billingReminder.classCode }} ·
                      {{ billingReminder.partnerName }}
                    </div>
                  </div>

                  <div class="dashboard-billing__actions">
                    <v-chip
                      x-small
                      dark
                      :color="billingStatusColor(billingReminder.status)"
                      class="mb-2"
                    >
                      {{ billingStatusLabel(billingReminder.status) }}
                    </v-chip>

                    <v-btn
                      rounded
                      x-small
                      outlined
                      color="primary-500"
                      @click="$router.push('/connect/aluno/financeiro')"
                    >
                      Ver
                    </v-btn>
                  </div>
                </div>

                <v-alert v-else type="success" text dense class="mb-0">
                  Você não possui cobranças pendentes no momento.
                </v-alert>
              </v-card-text>
            </v-card>

            <v-card class="dashboard-surface dashboard-week" elevation="0">
              <v-card-text class="pa-5">
                <div class="dashboard-section__title mb-3">
                  <v-icon color="secondary-500" class="mr-2"
                    >mdi-calendar-week-outline</v-icon
                  >
                  Aulas da semana
                </div>

                <div
                  v-if="dashboard.weekLessons.length"
                  class="dashboard-week__list"
                >
                  <div
                    v-for="lesson in dashboard.weekLessons"
                    :key="lesson.id"
                    class="dashboard-week__item"
                  >
                    <div class="dashboard-week__date-pill">
                      <div class="dashboard-week__weekday">
                        {{ formatWeekday(lesson.start) }}
                      </div>
                      <div class="dashboard-week__day">
                        {{ formatDayMonth(lesson.start) }}
                      </div>
                    </div>

                    <div class="dashboard-week__content">
                      <div class="dashboard-week__title">
                        {{ lesson.name }}
                      </div>
                      <div class="dashboard-week__meta">
                        {{ formatHour(lesson.start) }} às
                        {{ formatHour(lesson.end) }}
                      </div>
                    </div>

                    <div class="dashboard-week__actions">
                      <v-btn
                        v-if="lesson.link"
                        icon
                        small
                        color="secondary-500"
                        @click="openLessonLink(lesson.link)"
                      >
                        <v-icon small>mdi-video-outline</v-icon>
                      </v-btn>

                      <v-btn
                        icon
                        small
                        color="primary-500"
                        :disabled="!lesson.material?.moduleId"
                        @click="openLessonMaterial(lesson)"
                      >
                        <v-icon small>mdi-book-open-variant</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>

                <v-alert v-else type="info" text dense class="mb-0">
                  Nenhuma aula programada para os próximos dias.
                </v-alert>
              </v-card-text>
            </v-card>
          </div>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import pitchCustomerApi from "@/shared/pitchCustomerApi";

export default {
  name: "CustomerDashboard",

  async created() {
    await this.loadData();
  },

  data() {
    return {
      loading: true,
      dashboard: {
        module: null,
        progress: {
          totalLessons: 0,
          completedCount: 0,
          remainingCount: 0,
          completedPercentage: 0,
          completedClasses: [],
          remainingClasses: [],
        },
        overallProgress: {
          fromLevel: null,
          totalLessons: 0,
          completedCount: 0,
          remainingCount: 0,
          completedPercentage: 0,
          levels: [],
        },
        weekLessons: [],
        billingReminder: null,
      },
    };
  },

  computed: {
    customerName() {
      return this.$store.getters.getName?.split(" ")[0] || "Aluno";
    },
    greetingTitle() {
      return `Olá, ${this.customerName}`;
    },
    greetingSubtitle() {
      if (this.dashboard.module?.title) {
        return `Acompanhe o avanço do módulo ${this.dashboard.module.title} e as aulas desta semana.`;
      }

      return "Assim que seu módulo estiver ativo, você verá aqui o seu progresso.";
    },
    hasModule() {
      return !!this.dashboard.module;
    },
    progress() {
      return (
        this.dashboard.progress || {
          totalLessons: 0,
          completedCount: 0,
          remainingCount: 0,
          completedPercentage: 0,
          completedClasses: [],
          remainingClasses: [],
        }
      );
    },
    billingReminder() {
      return this.dashboard.billingReminder || null;
    },
    overallProgress() {
      return (
        this.dashboard.overallProgress || {
          fromLevel: null,
          totalLessons: 0,
          completedCount: 0,
          remainingCount: 0,
          completedPercentage: 0,
          levels: [],
        }
      );
    },
    hasOverallProgress() {
      return Number(this.overallProgress.totalLessons || 0) > 0;
    },
    donutStyle() {
      return this.buildDonutStyle(this.progress.completedPercentage);
    },
    overallDonutStyle() {
      return this.buildDonutStyle(
        this.overallProgress.completedPercentage,
        "#db2777",
        "rgba(219, 39, 119, 0.14)"
      );
    },
  },

  methods: {
    async loadData() {
      this.loading = true;

      try {
        const res = await pitchCustomerApi.getDashboard();
        this.dashboard = {
          module: res.data?.module || null,
          progress: res.data?.progress || this.dashboard.progress,
          overallProgress:
            res.data?.overallProgress || this.dashboard.overallProgress,
          weekLessons: Array.isArray(res.data?.weekLessons)
            ? res.data.weekLessons
            : [],
          billingReminder: res.data?.billingReminder || null,
        };
      } catch (error) {
        this.$root.$emit("display-alert", {
          text: "Nao foi possivel carregar o dashboard do aluno.",
          type: 1,
        });
      } finally {
        this.loading = false;
      }
    },
    openLessonMaterial(lesson) {
      if (!lesson?.material?.moduleId) {
        return;
      }

      this.openCourseContent(lesson.material, lesson.link);
    },
    openCourseContent(material, classLink = null) {
      if (!material?.moduleId) {
        return;
      }

      const query = {};

      if (material.materialId) {
        query.material = material.materialId;
      }

      if (classLink) {
        query.classLink = classLink;
      }

      this.$router.push({
        path: `/connect/aluno/curso/${material.moduleId}`,
        query,
      });
    },
    openLessonLink(link) {
      if (!link) {
        return;
      }

      window.open(link, "_blank");
    },
    parseDate(value) {
      if (!value) {
        return null;
      }

      if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
        const [year, month, day] = String(value).split("-").map(Number);
        return new Date(year, month - 1, day, 12, 0, 0, 0);
      }

      const parsed = new Date(String(value).replace(" ", "T"));

      return Number.isNaN(parsed.getTime()) ? null : parsed;
    },
    formatWeekday(value) {
      const date = this.parseDate(value);

      if (!date) {
        return "-";
      }

      return new Intl.DateTimeFormat("pt-BR", {
        weekday: "short",
      })
        .format(date)
        .replace(".", "")
        .toUpperCase();
    },
    formatDayMonth(value) {
      const date = this.parseDate(value);

      if (!date) {
        return "-";
      }

      return new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit",
        month: "2-digit",
      }).format(date);
    },
    formatHour(value) {
      const date = this.parseDate(value);

      if (!date) {
        return "--:--";
      }

      return new Intl.DateTimeFormat("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
    },
    formatPercentage(value) {
      return `${Number(value || 0).toFixed(0)}%`;
    },
    buildDonutStyle(
      value,
      completedColor = "#ec4899",
      trackColor = "rgba(244, 114, 182, 0.16)"
    ) {
      const percentage = Number(value || 0);
      const completedAngle = (percentage / 100) * 360;

      return {
        background: `conic-gradient(${completedColor} 0deg ${completedAngle}deg, ${trackColor} ${completedAngle}deg 360deg)`,
      };
    },
    levelBarColor(percentage) {
      const value = Number(percentage || 0);

      if (value >= 100) {
        return "success";
      }

      if (value > 0) {
        return "primary-500";
      }

      return "grey lighten-1";
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(Number(value || 0));
    },
    billingStatusLabel(status) {
      return status === "OVERDUE" ? "Atrasada" : "Pendente";
    },
    billingStatusColor(status) {
      return status === "OVERDUE" ? "error" : "warning";
    },
  },
};
</script>

<style scoped>
.customer-dashboard {
  width: 100%;
}

.dashboard-hero {
  border-radius: 24px;
  overflow: hidden;
  background: radial-gradient(
      circle at top right,
      rgba(255, 255, 255, 0.28),
      transparent 28%
    ),
    linear-gradient(135deg, rgba(236, 72, 153, 0.96), rgba(244, 114, 182, 0.88));
  box-shadow: 0 20px 44px -34px rgba(236, 72, 153, 0.7);
}

.dashboard-hero__content {
  min-height: 130px;
  padding: 1.4rem 1.8rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
}

.dashboard-hero__eyebrow {
  color: rgba(255, 255, 255, 0.82);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.72rem;
  font-weight: 700;
}

.dashboard-hero .text-h5,
.dashboard-hero__subtitle {
  color: #ffffff;
}

.dashboard-hero__subtitle {
  max-width: 620px;
  line-height: 1.5;
  font-size: 0.92rem;
}

.dashboard-hero__avatar {
  background: rgba(15, 23, 42, 0.18);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.18);
}

.dashboard-surface {
  height: 100%;
  border-radius: 22px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.98) 0%,
    rgba(255, 241, 246, 0.92) 100%
  );
  border: 1px solid rgba(244, 114, 182, 0.12);
  box-shadow: 0 18px 44px -46px rgba(15, 23, 42, 0.42);
}

.dashboard-week {
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.98) 0%,
    rgba(254, 242, 248, 0.9) 100%
  );
}

.dashboard-billing {
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.98) 0%,
    rgba(255, 247, 237, 0.96) 100%
  );
}

.dashboard-side-column {
  display: grid;
  gap: 1rem;
}

.dashboard-section__title {
  display: flex;
  align-items: center;
  font-size: 1.05rem;
  font-weight: 700;
  color: #111827;
}

.dashboard-section__subtitle {
  margin-top: 0.2rem;
  color: rgba(31, 41, 55, 0.66);
  line-height: 1.4;
  font-size: 0.85rem;
}

.dashboard-progress-head {
  margin-bottom: 1rem;
}

/* ------- Donuts (Geral + Nivel atual) ------- */
.dashboard-donuts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 1.2rem;
}

.dashboard-donut {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.75rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(244, 114, 182, 0.12);
}

.dashboard-donut__ring {
  width: 118px;
  height: 118px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dashboard-donut__inner {
  width: 78px;
  height: 78px;
  border-radius: 999px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: inset 0 0 0 1px rgba(244, 114, 182, 0.12);
}

.dashboard-donut__value {
  font-size: 1.3rem;
  font-weight: 700;
  color: #111827;
}

.dashboard-donut__label {
  margin-top: 0.6rem;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #be185d;
}

.dashboard-donut__meta {
  margin-top: 0.15rem;
  font-size: 0.78rem;
  color: rgba(31, 41, 55, 0.6);
}

/* ------- Barras por nivel ------- */
.dashboard-levels {
  padding: 0.9rem 1rem;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(244, 114, 182, 0.12);
}

.dashboard-levels__title {
  font-size: 0.88rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.75rem;
}

.dashboard-levels__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem 1.25rem;
}

.dashboard-levels__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.3rem;
}

.dashboard-levels__name {
  font-size: 0.82rem;
  font-weight: 600;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dashboard-levels__meta {
  font-size: 0.78rem;
  font-weight: 700;
  color: #be185d;
  white-space: nowrap;
  margin-left: 0.5rem;
}

/* ------- Mensalidade ------- */
.dashboard-billing__item {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.75rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.86);
  border: 1px solid rgba(251, 146, 60, 0.18);
}

.dashboard-billing__date-pill {
  min-width: 62px;
  padding: 0.55rem 0.6rem;
  border-radius: 14px;
  text-align: center;
  background: linear-gradient(180deg, #fb923c 0%, #f97316 100%);
  color: #ffffff;
}

.dashboard-billing__weekday {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.06em;
}

.dashboard-billing__day {
  margin-top: 0.2rem;
  font-size: 0.92rem;
  font-weight: 700;
}

.dashboard-billing__title {
  color: #111827;
  font-size: 1.05rem;
  font-weight: 700;
}

.dashboard-billing__meta {
  margin-top: 0.15rem;
  color: rgba(31, 41, 55, 0.68);
  font-weight: 600;
  font-size: 0.8rem;
}

.dashboard-billing__actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

/* ------- Aulas da semana ------- */
.dashboard-week__list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  max-height: 260px;
  overflow: auto;
}

.dashboard-week__item {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.65rem 0.75rem;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(244, 114, 182, 0.1);
}

.dashboard-week__date-pill {
  min-width: 58px;
  padding: 0.5rem 0.45rem;
  border-radius: 14px;
  text-align: center;
  background: linear-gradient(
    180deg,
    rgba(236, 72, 153, 0.16) 0%,
    rgba(244, 114, 182, 0.06) 100%
  );
}

.dashboard-week__weekday {
  font-size: 0.68rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 700;
  color: #be185d;
}

.dashboard-week__day {
  margin-top: 0.15rem;
  font-size: 0.9rem;
  font-weight: 700;
  color: #111827;
}

.dashboard-week__title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dashboard-week__meta {
  margin-top: 0.1rem;
  color: rgba(31, 41, 55, 0.64);
  font-weight: 600;
  font-size: 0.8rem;
}

.dashboard-week__actions {
  display: flex;
  align-items: center;
}

@media (max-width: 600px) {
  .dashboard-levels__grid {
    grid-template-columns: 1fr;
  }

  .dashboard-hero__content {
    flex-direction: column;
    align-items: flex-start;
  }

  .dashboard-billing__item,
  .dashboard-week__item {
    grid-template-columns: auto 1fr auto;
  }
}
</style>
